import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import request from '../../../utils/Request.utils';
import Modal from "../../component/Modal";
import Chat from "../../component/Chat";

// 회원 상태 코드 표시 (US10: 정상, US90: 탈퇴)
const USER_STAT_LABEL = {
	US10: '정상',
	US90: '탈퇴',
};
const getUserStatLabel = (stat) => USER_STAT_LABEL[stat] || (stat ? stat : '-');
const NUMERIC_FIELDS = ['user_corp_no', 'user_payment_bank_no'];
// 서버에서 기본 마스킹되어 내려오는 민감 필드 (관리자 '눈 토글'로 원본 열람)
const SENSITIVE_FIELDS = ['user_corp_no', 'user_payment_bank_no'];
const sanitizeDigits = (value) => String(value || '').replace(/\D/g, '');
const formatKoreanDateTime = (value) => {
	if (!value) return '-';

	const rawValue = String(value).trim();
	const hasTime = rawValue.includes(':');
	if (!hasTime) return rawValue;

	const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(rawValue);
	const normalized = rawValue.replace(' ', 'T');
	const parseValue = hasTimezone ? normalized : `${normalized}Z`;
	const date = new Date(parseValue);

	if (Number.isNaN(date.getTime())) return rawValue;

	return date.toLocaleString('ko-KR', {
		timeZone: 'Asia/Seoul',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
};
const DEFAULT_USER_INFO = {
	"2nd_certi_YN": "",
	"2nd_certi_ty": "",
	"agree_ads": "",
	"agree_privacy_policy": "",
	"agree_terms": "",
	"agree_third_party_sharing": "",
	"chat_online_YN": null,
	"chat_room_no": null,
	"fail_cnt": 0,
	"join_ty": "",
	"last_login_dt": "",
	"last_login_ip": "",
	"login_cnt": 0,
	"reg_dtm": "",
	"user_email": "",
	"user_id": "",
	"user_phone": "",
	"user_pwd": "",
	"user_seq": 0,
	"user_stat": "",
	"user_ty": "",
	"user_corp": "",
	"user_corp_phone": "",
	"user_corp_no": "",
	"user_corp_director": "",
	"user_payment_card_seq": "",
	"user_payment_bank_no": "",
	"user_payment_bank_name": "",
	"user_payment_bank_cd": "",
};

const MemberDetail = () => {
	const navigate = useNavigate();
	const { user_seq } = useParams();

	const [userInfo, setUserInfo] = useState(DEFAULT_USER_INFO);
	const [pointChange, setPointChange] = useState(false);
	const [chattingView, setChattingView] = useState(false);

	// 추가정보
	const [bankList, setBankList] = useState([]);
	const [selectedBankCode, setSelectedBankCode] = useState('');

	// 민감 필드 마스킹/열람 상태
	const [maskedFields, setMaskedFields] = useState([]);       // 서버가 마스킹해 내려준 필드(_masked)
	const [revealed, setRevealed] = useState({});               // { field: true } 현재 원본 열람중
	const [maskedSnapshot, setMaskedSnapshot] = useState({});   // 마스킹 원문(숨길 때 복원용)

	const isMasked = (field) => maskedFields.includes(field);
	const isHidden = (field) => isMasked(field) && !revealed[field]; // 지금 가려진 상태인지

	const fetchUserData = useCallback(async () => {
		try {
			const successHandler = (response) => {
				console.log('response', response.data)
				if (response.messageCode === 200) {
					const data = response.data || {};
					const nextUserInfo = {
						...DEFAULT_USER_INFO,
						...Object.keys(DEFAULT_USER_INFO).reduce((acc, key) => {
							acc[key] = data[key] ?? DEFAULT_USER_INFO[key];
							return acc;
						}, {})
					};
					setUserInfo(nextUserInfo);
					setSelectedBankCode(data.user_payment_bank_cd || '');

					// 서버가 마스킹한 필드 목록과 마스킹 원문을 저장하고, 열람 상태는 초기화(기본 가림)
					const maskedList = Array.isArray(data._masked) ? data._masked : [];
					setMaskedFields(maskedList);
					setRevealed({});
					setMaskedSnapshot(maskedList.reduce((acc, f) => {
						acc[f] = data[f] ?? '';
						return acc;
					}, {}));
				} else {
					toast.error("회원 정보를 불러올 수 없습니다.");
				}
			};

			const errorHandler = (error) => {
				toast.error("회원 정보를 불러오는 중 오류가 발생했습니다.");
			};

			await request.get(`/user/info-with-detail/${user_seq}`, {}, successHandler, errorHandler);
		} catch (error) {
			toast.error("회원 정보를 불러오는 중 오류가 발생했습니다.");
		}
	}, [user_seq]);

	const getBankInfo = useCallback(() => {
		let params = {};

		const successHandler = (response) => {
			if (response.messageCode === 200) {
				setBankList(response.data.data_list);
			} else {
				console.log('은행 정보를 불러올수없습니다.');
				toast.error('은행 정보를 불러올수없습니다.');
			}
		};
		const failHandler = (error) => {
			console.log(error);
		};

		request.get('/common/code/bank_cd', params, successHandler, failHandler);
	}, []);

	useEffect(() => {
		getBankInfo();
		fetchUserData();
	}, [getBankInfo, fetchUserData]);

	// 민감 필드 '눈 토글': 열람 시 서버(reveal API)에서 원본을 받아 표시, 다시 누르면 마스킹으로 복원
	const toggleReveal = (field) => {
		if (revealed[field]) {
			setUserInfo((prev) => ({ ...prev, [field]: maskedSnapshot[field] ?? '' }));
			setRevealed((prev) => ({ ...prev, [field]: false }));
			return;
		}

		const successHandler = (response) => {
			if (response.messageCode === 200 && response.data) {
				setUserInfo((prev) => ({ ...prev, [field]: response.data.value ?? '' }));
				setRevealed((prev) => ({ ...prev, [field]: true }));
			} else {
				toast.error('원본을 불러올 수 없습니다.');
			}
		};
		const errorHandler = () => toast.error('원본 조회 중 오류가 발생했습니다.');

		request.get(`/user/reveal/${user_seq}`, { field }, successHandler, errorHandler);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		const nextValue = NUMERIC_FIELDS.includes(name) ? sanitizeDigits(value) : value;
		setUserInfo((prev) => ({ ...prev, [name]: nextValue }));
	};

	const handleSubmit = async () => {
		try {
			const normalizedUserInfo = {
				...userInfo,
				user_corp: String(userInfo.user_corp || '').trim(),
				user_corp_director: String(userInfo.user_corp_director || '').trim(),
				user_corp_no: sanitizeDigits(userInfo.user_corp_no),
				user_payment_bank_cd: selectedBankCode,
				user_payment_bank_name: String(userInfo.user_payment_bank_name || '').trim(),
				user_payment_bank_no: sanitizeDigits(userInfo.user_payment_bank_no),
			};

			// 열람하지 않은(가려진) 민감 필드는 마스킹 값이므로 저장 대상에서 제외한다.
			// → 백엔드는 해당 필드를 건드리지 않아 기존 원본이 그대로 보존된다. (마스킹값 저장으로 인한 손상 방지)
			const hiddenSensitive = SENSITIVE_FIELDS.filter((field) => isHidden(field));
			const payload = { ...normalizedUserInfo };
			hiddenSensitive.forEach((field) => { delete payload[field]; });

			if (!hiddenSensitive.includes('user_corp_no')
				&& normalizedUserInfo.user_corp_no && normalizedUserInfo.user_corp_no.length !== 10) {
				toast.error("사업자번호는 숫자 10자리로 입력해 주세요.");
				return;
			}

			if (!hiddenSensitive.includes('user_payment_bank_no')) {
				if (normalizedUserInfo.user_payment_bank_no && !normalizedUserInfo.user_payment_bank_cd) {
					toast.error("계좌번호를 입력하려면 은행을 선택해 주세요.");
					return;
				}

				if (normalizedUserInfo.user_payment_bank_cd && !normalizedUserInfo.user_payment_bank_no) {
					toast.error("은행을 선택한 경우 계좌번호를 입력해 주세요.");
					return;
				}
			}

			const successHandler = (response) => {
				console.log(response)
				if (response.messageCode === 200) {
					toast.success("회원 정보가 성공적으로 수정되었습니다.");
					fetchUserData();
					// navigate("/Member/MemberManage");
				} else if (response.messageCode === 411) {
					toast.error("변경사항이 없습니다");
				}
			};

			const errorHandler = (error) => {
				toast.error("회원 정보 수정 중 오류가 발생했습니다.");
			};

			await request.put(`/user/info-with-detail/${user_seq}`, payload, successHandler, errorHandler);
		} catch (error) {
			toast.error("회원 정보 수정 중 오류가 발생했습니다.");
		}
	};

	// 은행선택
	const handleBankChange = (event) => {
		setSelectedBankCode(event.target.value);
		setUserInfo((prev) => ({ ...prev, user_payment_bank_cd: event.target.value }));
	};


	// 민감 필드 옆 '눈' 버튼 (마스킹된 필드에만 노출)
	// 사업자번호: 행 전체로 늘어나지 않도록 폭 제한
	const corpWrapStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%', maxWidth: 420 };
	// 계좌 셀(grid)에 눈 버튼 열을 하나 더 추가 (마스킹일 때만 auto 열 부여)
	const bankGridStyle = (masked) => ({
		gridTemplateColumns: masked ? '150px minmax(0, 1fr) minmax(0, 1fr) auto' : undefined,
	});
	const revealBtnStyle = {
		flex: '0 0 auto', padding: '5px 9px', border: '1px solid #d0d5dd',
		background: '#fff', borderRadius: 6, cursor: 'pointer', lineHeight: 1, fontSize: 15,
	};
	const renderEye = (field) => {
		if (!isMasked(field)) return null;
		const on = !!revealed[field];
		return (
			<button
				type="button"
				style={revealBtnStyle}
				onClick={() => toggleReveal(field)}
				title={on ? '가리기' : '원본 보기'}
				aria-label={on ? '가리기' : '원본 보기'}
			>
				{on ? '🙈' : '👁'}
			</button>
		);
	};

	return (
		<main className="adm_member_detail">
			<h2 className="adm_com_pg_tit">회원 관리</h2>
			<h3 className="adm_com_pg_tit__wrap margin_top">- 회원기본정보
				<div className="adm_com_btn_wrap right">
					<button className="adm_com_btn gray s" onClick={handleSubmit}>수정</button>
					<button onClick={() => navigate("/Member/MemberManage")} className="adm_com_btn blue s">목록</button>
				</div>
			</h3>
			<table className='adm_com_board_list type_row'>
				<colgroup>
					<col width="10%" /><col width="40%" />
					<col width="10%" /><col width="40%" />
				</colgroup>
				<tbody>
					<tr>
						<th>아이디</th>
						<td>{userInfo.user_id}</td>
						<th>이메일</th>
						<td>{userInfo.user_email || '-'}</td>
					</tr>
					<tr>
						<th>휴대폰</th>
						<td>
							<input type="text" className="input wl" name="user_phone" value={userInfo.user_phone} readOnly />
						</td>
						<th>상태</th>
						<td>{getUserStatLabel(userInfo.user_stat)}</td>
					</tr>
					<tr>
						<th>최근접속일</th>
						<td>{formatKoreanDateTime(userInfo.last_login_dt)}</td>
						<th>가입일자</th>
						<td>{formatKoreanDateTime(userInfo.reg_dtm)}</td>
					</tr>
				</tbody>
			</table>
			<h3 className="adm_com_pg_tit__wrap margin_top">- 회원추가정보
			</h3>
			<table className='adm_com_board_list type_row'>
				<colgroup>
					<col width="10%" /><col width="40%" />
					<col width="10%" /><col width="40%" />
				</colgroup>
				<tbody>
					<tr>
						<th>회사명</th>
						<td>
							<input type="text" className="input wl" name="user_corp" value={userInfo.user_corp} onChange={handleChange} />
						</td>
						<th>대표자명</th>
						<td><input type="text" className="input wl" name="user_corp_director" value={userInfo.user_corp_director} onChange={handleChange} /></td>
					</tr>
					<tr>
						<th>사업자번호</th>
						<td colSpan="3">
							<span style={corpWrapStyle}>
								<input type="text" className="input wl" name="user_corp_no" value={userInfo.user_corp_no} onChange={handleChange} readOnly={isHidden('user_corp_no')} inputMode="numeric" pattern="[0-9]*" maxLength={10} style={{ flex: 1, minWidth: 0 }} />
								{renderEye('user_corp_no')}
							</span>
						</td>
					</tr>
					<tr>
						<th>계좌</th>
						<td colSpan="3" className="bank_info_cell" style={bankGridStyle(isMasked('user_payment_bank_no'))}>
							<select
								className="select ws"
								value={selectedBankCode}
								onChange={handleBankChange}
							>
								<option value="">은행선택</option>
								{bankList.map((bank) => (
									<option key={bank.comm_code} value={bank.comm_code}>
										{bank.code_nm}
									</option>
								))}
							</select>
							<input
								type="text"
								className="input wl"
								placeholder="예금주 입력"
								name="user_payment_bank_name"
								value={userInfo.user_payment_bank_name}
								onChange={handleChange}
							/>
							<input
								type="text"
								className="input wl"
								placeholder="계좌번호 입력"
								name="user_payment_bank_no"
								value={userInfo.user_payment_bank_no}
								onChange={handleChange}
								readOnly={isHidden('user_payment_bank_no')}
								inputMode="numeric"
								pattern="[0-9]*"
								maxLength={30}
							/>
							{renderEye('user_payment_bank_no')}
						</td>
					</tr>
				</tbody>
			</table>
			<Modal open={pointChange} close={() => setPointChange(false)} popSize="m">
				<div className="pop_tit">포인트 변동하기</div>
				<div className="content_container">
					<table className='adm_com_board_list type_row'>
						<colgroup>
							<col width="20%" /><col />
						</colgroup>
						<tbody>
							<tr>
								<th>종류</th>
								<td>
									<div className="adm_com_input_wrap">
										<input type="checkbox" className="adm_com_chk circle" id="point01" name="chk_point" value="Y" />
										<label htmlFor="point01">충전</label>
										<input type="checkbox" className="adm_com_chk circle" id="point02" name="chk_point" value="Y" />
										<label htmlFor="point02">차감</label>
									</div>
								</td>
							</tr>
							<tr>
								<th>금액</th>
								<td><input type="text" className="input" defaultValue={'30,000'} /></td>
							</tr>
							<tr>
								<th>변동내용</th>
								<td>
									<textarea className="textarea">포인트 환불</textarea>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div className="btn_container">
					<button className="adm_com_btn blue">확인</button>
					<button onClick={() => setPointChange(false)} className="adm_com_btn gray">취소</button>
				</div>
			</Modal>
			<Modal open={chattingView} close={() => setChattingView(false)} popSize="m">
				<Chat />
				<div className="btn_container">
					<button onClick={() => setChattingView(false)} className="adm_com_btn s gray">닫기</button>
				</div>
			</Modal>
			<ToastContainer />
		</main>
	);
};

export default MemberDetail;
